import { useNavigate } from 'react-router-dom';
import { useUserContext } from './context/UserContext'; 
import React from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Switch, 
  FormControlLabel, 
  IconButton,
  Autocomplete,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VerifiedIcon from '@mui/icons-material/Verified';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  addDoc, 
  query, 
  where, 
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { firestore } from '../firebase.config';

// Add this style tag for the spinning animation
const spinKeyframes = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

// Insert the style into the document head
if (typeof document !== 'undefined' && !document.getElementById('spin-animation')) {
  const style = document.createElement('style');
  style.id = 'spin-animation';
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

const ProductHierarchyManager = () => {
  const navigate = useNavigate();
  const { userProfile } = useUserContext();
  // State for form fields - using level terminology
  const [products, setProducts] = React.useState([
    {
      id: 1,
      name: '',
      description: '',
      moq: '',
      sgst: '',
      cgst: '',
      igst: '',
      gst: '', // Total GST (auto-calculated)
      hsn: '',
      price: '',
      cost: '',
      margin: '',
      isStandard: true,
      hasSubProducts: false,
      isLevel1: true,
      nameError: false,
      descriptionError: false,
      moqError: false,
      sgstError: false,
      cgstError: false,
      igstError: false,
      gstError: false,
      hsnError: false,
      priceError: false,
      costError: false,
      marginError: false
    }
  ]);

  // State for existing products from Firebase
  const [existingProducts, setExistingProducts] = React.useState([]);
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [searchFilter, setSearchFilter] = React.useState('name');
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  
  // Individual filter menu states
  const [priceMenuAnchor, setPriceMenuAnchor] = React.useState(null);
  const [gstMenuAnchor, setGstMenuAnchor] = React.useState(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = React.useState(null);
  const [allFiltersMenuAnchor, setAllFiltersMenuAnchor] = React.useState(null);
  
  // Selected filter values
  const [selectedPriceRange, setSelectedPriceRange] = React.useState('');
  const [selectedGstValue, setSelectedGstValue] = React.useState('');
  const [selectedType, setSelectedType] = React.useState('');
  const autocompleteRef = React.useRef(null);

  // Price range options
  const priceRanges = [
    { value: '0-20', label: '₹0 - ₹20', min: 0, max: 20 },
    { value: '21-40', label: '₹21 - ₹40', min: 21, max: 40 },
    { value: '41-60', label: '₹41 - ₹60', min: 41, max: 60 },
    { value: '61-80', label: '₹61 - ₹80', min: 61, max: 80 },
    { value: '81-100', label: '₹81 - ₹100', min: 81, max: 100 },
    { value: '100+', label: '₹100+', min: 100, max: Infinity }
  ];

  // GST options
  const gstOptions = [
    { value: '0', label: '0% GST' },
    { value: '5', label: '5% GST' },
    { value: '12', label: '12% GST' },
    { value: '18', label: '18% GST' },
    { value: '28', label: '28% GST' }
  ];

  // Type options - updated to std/derived
  const typeOptions = [
    { value: 'std', label: 'Std Products', color: 'success' },
    { value: 'derived', label: 'Derived Products', color: 'warning' }
  ];

  // Handle menu functions
  const handlePriceMenuOpen = (event) => setPriceMenuAnchor(event.currentTarget);
  const handlePriceMenuClose = () => setPriceMenuAnchor(null);
  const handleGstMenuOpen = (event) => setGstMenuAnchor(event.currentTarget);
  const handleGstMenuClose = () => setGstMenuAnchor(null);
  const handleTypeMenuOpen = (event) => setTypeMenuAnchor(event.currentTarget);
  const handleTypeMenuClose = () => setTypeMenuAnchor(null);
  const handleAllFiltersMenuOpen = (event) => setAllFiltersMenuAnchor(event.currentTarget);
  const handleAllFiltersMenuClose = () => setAllFiltersMenuAnchor(null);

  // Handle filter selection
  const handlePriceSelect = (priceRange) => {
    setSelectedPriceRange(priceRange);
    setSearchFilter('price');
    handlePriceMenuClose();
  };

  const handleGstSelect = (gst) => {
    setSelectedGstValue(gst);
    setSearchFilter('gst');
    handleGstMenuClose();
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSearchFilter(type);
    handleTypeMenuClose();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedPriceRange('');
    setSelectedGstValue('');
    setSelectedType('');
    setSearchFilter('name');
    handleAllFiltersMenuClose();
  };

  // Helper function to create unique identifier for products
  const createProductKey = (product) => {
    if (product.source === 'main_collection') {
      return `main_${product.id}`;
    } else {
      return `sub_${product.parentId || 'unknown'}_${product.source}_${product.id}`;
    }
  };

  // Helper function to check if current ID equals original ID
  const hasMatchingIds = (product) => {
    // For main collection products, current ID should equal original ID
    if (product.source === 'main_collection') {
      return product.id === product.originalId;
    }
    
    // For subcollection products, check if they are references or originals
    // If it's a reference, current ID might differ from original ID
    // If it's an original product, current ID should equal original ID
    if (product.isReference === true) {
      // References are expected to have different IDs, so we skip this check
      return false; // Don't show references in dropdown
    }
    
    // For non-reference subcollection products, current ID should equal original ID
    return product.id === product.originalId;
  };

  // Fetch products from Firebase
  const fetchExistingProducts = React.useCallback(async () => {
    setLoadingProducts(true);
    try {
      const productsArray = [];
      const productKeys = new Set(); // Track unique product keys to avoid duplicates
      
      // Get ALL products from Catalogue collection
      const allProductsQuery = collection(firestore, "Catalogue");
      const allProductsSnapshot = await getDocs(allProductsQuery);
      
      allProductsSnapshot.forEach((doc) => {
        const data = doc.data();
        const productKey = `main_${doc.id}`;
        
        // Skip if we've already seen this product
        if (productKeys.has(productKey)) {
          console.log(`Skipping duplicate Level1 product: ${data.name} (ID: ${doc.id})`);
          return;
        }
        
        const computedPrice = data.cost ? (Number(data.cost) * (1 + (Number(data.margin || 0) / 100))).toFixed(2) : (data.price || '');
        const product = {
          id: doc.id,
          originalId: doc.id, // PRESERVE ORIGINAL ID
          name: data.name,
          isStandard: data.isStandard,
          description: data.description || '',
          gst: data.gst || '',
          hsn: data.hsn || '',
          price: computedPrice,
          cost: data.cost || '',
          margin: data.margin || '',
          moq: data.moq || '',
          source: 'main_collection',
          isReference: false,
          uniqueKey: productKey, // ADD UNIQUE KEY FOR TRACKING
          label: `${data.name} (${data.isStandard ? 'Std' : 'Derived'}) - Price: ₹${computedPrice || 'N/A'} - Cost: ₹${data.cost || 'N/A'} - Margin: ${data.margin ? data.margin + '%' : 'N/A'} - MOQ: ${data.moq || 'N/A'}`,
          searchText: `${data.name} ${data.description || ''} ${data.gst || ''} ${data.hsn || ''} ${data.price || ''} ${data.cost || ''} ${data.margin || ''} ${data.isStandard ? 'std standard' : 'derived'}`
        };

        // FILTER: Only add products where current ID equals original ID
        if (hasMatchingIds(product)) {
          productKeys.add(productKey);
          productsArray.push(product);
        } else {
          console.log(`Filtered out Level1 product with mismatched IDs: ${data.name} (Current: ${doc.id}, Original: ${doc.id})`);
        }
      });

      // Check subcollections for Level2 products
      const parentProductsQuery = query(collection(firestore, "Catalogue"), where("isStandard", "==", false));
      const parentProductsSnapshot = await getDocs(parentProductsQuery);
      
      for (const parentDoc of parentProductsSnapshot.docs) {
        const subcollections = ['SubCollection1', 'SubCollection2', 'SubCollection3', 'SubCollection4', 'SubCollection5'];
        
        for (let i = 0; i < subcollections.length; i++) {
          const subCollectionRef = collection(parentDoc.ref, subcollections[i]);
          const subCollectionSnapshot = await getDocs(subCollectionRef);
          
          subCollectionSnapshot.forEach((subDoc) => {
            const data = subDoc.data();
            const parentName = parentDoc.data().name;
            const productKey = `sub_${parentDoc.id}_${subcollections[i].toLowerCase()}_${subDoc.id}`;
            
            // Skip if we've already seen this product
            if (productKeys.has(productKey)) {
              console.log(`Skipping duplicate Level2 product: ${data.name} (ID: ${subDoc.id})`);
              return;
            }
            
            // Handle both regular products and references
            const isReference = data.isReference === true;
            const computedPrice = data.cost ? (Number(data.cost) * (1 + (Number(data.margin || 0) / 100))).toFixed(2) : (data.price || '');
            const displayLabel = `${data.name} (${data.isStandard ? 'Std' : 'Derived'}) - Price: ₹${computedPrice || 'N/A'} - Cost: ₹${data.cost || 'N/A'} - Margin: ${data.margin ? data.margin + '%' : 'N/A'} - MOQ: ${data.moq || 'N/A'} - Child of: ${parentName}`;

            const product = {
              id: subDoc.id,
              originalId: data.originalProductId || subDoc.id, // PRESERVE ORIGINAL ID OR USE CURRENT
              name: data.name,
              isStandard: data.isStandard,
              description: data.description || '',
              gst: data.gst || '',
              hsn: data.hsn || '',
              price: computedPrice,
              cost: data.cost || '',
              margin: data.margin || '',
              moq: data.moq || '',
              parentId: data.parentId,
              parentName: parentName,
              source: subcollections[i].toLowerCase(),
              isReference: isReference,
              originalProductId: data.originalProductId || null,
              originalSource: data.originalSource || null,
              uniqueKey: productKey, // ADD UNIQUE KEY FOR TRACKING
              label: displayLabel,
              searchText: `${data.name} ${data.description || ''} ${parentName} ${data.gst || ''} ${data.hsn || ''} ${computedPrice || ''} ${data.cost || ''} ${data.margin || ''} ${data.isStandard ? 'std standard' : 'derived'} ${isReference ? 'reference' : 'original'} level2 subcollection`
            };

            // FILTER: Only add products where current ID equals original ID (and not references)
            if (hasMatchingIds(product)) {
              productKeys.add(productKey);
              productsArray.push(product);
            } else {
              console.log(`Filtered out Level2 product with mismatched IDs or reference: ${data.name} (Current: ${subDoc.id}, Original: ${data.originalProductId || subDoc.id}, IsReference: ${isReference})`);
            }
          });
        }
      }

      // Sort products - prioritize non-references and remove any potential duplicates
      const uniqueProducts = productsArray.filter((product, index, self) => 
        index === self.findIndex(p => p.uniqueKey === product.uniqueKey)
      );
      
      uniqueProducts.sort((a, b) => {
        // First, sort by reference status (non-references first)
        if (a.isReference !== b.isReference) {
          return a.isReference ? 1 : -1;
        }
        
        // Then by name
        return a.name.localeCompare(b.name);
      });
      
      setExistingProducts(uniqueProducts);
      console.log(`Loaded ${uniqueProducts.length} unique products with matching IDs (filtered out references and mismatched ID products)`);
      
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Load products when component mounts or when Level1 product becomes derived
  React.useEffect(() => {
    const isLevel1ProductDerived = products[0]?.isStandard === false;
    if (isLevel1ProductDerived) {
      fetchExistingProducts();
    } else {
      setSelectedProducts([]);
    }
  }, [products, fetchExistingProducts, refreshTrigger]);

  const refreshExistingProducts = async () => {
    setLoadingProducts(true);
    // Clear current data first
    setExistingProducts([]);
    setSelectedProducts([]);
    
    // Force refresh by calling fetch directly
    await fetchExistingProducts();
    
    console.log("Products refreshed successfully - showing only products with matching IDs");
  };

  // Handle product selection with duplicate prevention
  const handleProductSelect = (event, selectedProductsList) => {
    // Remove duplicates based on uniqueKey
    const uniqueSelections = selectedProductsList.filter((product, index, self) => 
      index === self.findIndex(p => p.uniqueKey === product.uniqueKey)
    );
    
    // Also prevent selecting the same product as the Level1 product
    const level1ProductName = products[0]?.name?.trim().toLowerCase();
    const filteredSelections = uniqueSelections.filter(product => 
      product.name.trim().toLowerCase() !== level1ProductName
    );
    
    if (filteredSelections.length !== selectedProductsList.length) {
      console.log("Removed duplicate or conflicting product selections");
    }
    
    setSelectedProducts(filteredSelections);
  };
  
  // Validate product
  const validateProduct = (product, index) => {
    const updatedProducts = [...products];
    const current = updatedProducts[index];

    current.nameError = !current.name;
    current.descriptionError = !current.description;
    current.moqError = !current.moq;
    current.sgstError = !current.sgst;
    current.cgstError = !current.cgst;
    current.igstError = !current.igst;
    current.hsnError = !current.hsn;
    // price is computed from cost and margin; no direct price validation
    
    setProducts(updatedProducts);
    
    return !current.nameError && !current.descriptionError && !current.moqError && 
      !current.sgstError && !current.cgstError && !current.igstError && !current.hsnError;
  };
  
  // Handle field changes
  const handleChange = (index, field, value) => {
    const updatedProducts = [...products];
    updatedProducts[index][field] = value;
    const errorField = `${field}Error`;
    if (updatedProducts[index][errorField] !== undefined) {
      updatedProducts[index][errorField] = false;
    }
    
    // Auto-calculate total GST when SGST, CGST, or IGST changes
    if (field === 'sgst' || field === 'cgst' || field === 'igst') {
      const sgst = parseFloat(updatedProducts[index].sgst || 0);
      const cgst = parseFloat(updatedProducts[index].cgst || 0);
      const igst = parseFloat(updatedProducts[index].igst || 0);
      updatedProducts[index].gst = (sgst + cgst + igst).toFixed(2);
    }
    
    if (field === 'isStandard') {
      if (value === true) {
        // When switching to std, remove products after current index
        const productsToKeep = updatedProducts.slice(0, index + 1);
        setProducts(productsToKeep);
        
        // Clear selected products when switching Level1 product to std
        if (index === 0) {
          setSelectedProducts([]);
          if (autocompleteRef.current) {
            const input = autocompleteRef.current.querySelector('input');
            if (input) {
              input.blur();
            }
          }
        }
        return;
      } else {
        // When switching to derived, just clear selected products but don't auto-create products
        if (index === 0) {
          setSelectedProducts([]);
        }
      }
    }
    
    // If changing the Level1 product name, filter out any selected products with the same name
    if (index === 0 && field === 'name' && selectedProducts.length > 0) {
      const newName = value.trim().toLowerCase();
      const filteredSelections = selectedProducts.filter(product => 
        product.name.trim().toLowerCase() !== newName
      );
      if (filteredSelections.length !== selectedProducts.length) {
        setSelectedProducts(filteredSelections);
        console.log("Removed conflicting product selection due to name change");
      }
    }
    
    setProducts(updatedProducts);
  };
  
  // Add product
  const addProduct = () => {
    if (products.length === 0) return;
    
    const newProductId = products.length + 1;
    if (newProductId <= 6) {
      const newProduct = {
        id: newProductId,
        name: '',
        description: '',
        moq: '',
        gst: '',
        hsn: '',
        price: '',
        cost: '',
        margin: '',
        isStandard: true,
        hasSubProducts: false,
        isLevel1: newProductId === 1,
        nameError: false,
        descriptionError: false,
        moqError: false,
        gstError: false,
        hsnError: false,
        priceError: false,
        costError: false,
        marginError: false
      };
      
      setProducts([...products, newProduct]);
    } else {
      alert("Maximum products (6) reached");
    }
  };
  
  // Remove product
  const removeProduct = (index) => {
    if (products.length > 1) {
      const updatedProducts = [...products];
      updatedProducts.splice(index, 1);
      
      updatedProducts.forEach((product, idx) => {
        product.id = idx + 1;
        product.isLevel1 = idx === 0;
      });
      
      setProducts(updatedProducts);
      
      if (index === 1) {
        setSelectedProducts([]);
      }
    }
  };
  const tenantId = sessionStorage.getItem('tenantId'); // Get tenantId from sessionStorage

  // Reset form
  const resetForm = () => {
    setProducts([
      {
        id: 1,
        name: '',
        description: '',
        moq: '',
        sgst: '',
        cgst: '',
        igst: '',
        gst: '',
        hsn: '',
        price: '',
        cost: '',
        margin: '',
        isStandard: true,
        hasSubProducts: false,
        isLevel1: true,
        nameError: false,
        descriptionError: false,
        moqError: false,
        sgstError: false,
        cgstError: false,
        igstError: false,
        gstError: false,
        hsnError: false,
        priceError: false,
        costError: false,
        marginError: false
      }
    ]);
    setSelectedProducts([]);
  };
  
  // Submit form
  const handleSubmit = async () => {
    // Prefer tenantId from userProfile; fallback to sessionStorage if userProfile not yet populated
    console.log("Submitting with userProfile:", userProfile);
    const tenantId = userProfile?.tenantId || sessionStorage.getItem('tenantId');
    console.log("Tenant ID at time of submission:", tenantId);

    if (!tenantId) {
      alert("Tenant ID not found. Please wait for user data to load and try again.");
      return;
    }
  let isValid = true;
  
  products.forEach((product, index) => {
    if (!validateProduct(product, index)) {
      isValid = false;
    }
  });
    
    if (!isValid) {
      console.error("Form validation failed");
      return;
    }
    
    try {
      let createdDocRefs = {};
      
      // Handle Level1 Product
      const level1Product = products[0];
      const { name, description, moq, sgst, cgst, igst, gst, hsn, cost, margin, isStandard } = level1Product;
      const computedLevel1Price = cost ? (Number(cost) * (1 + (Number(margin || 0) / 100))).toFixed(2) : (level1Product.price || '');

      const level1ProductData = {
        name,
        description,
        moq,
        sgst: sgst || '',
        cgst: cgst || '',
        igst: igst || '',
        gst: gst || '',
        hsn,
        price: computedLevel1Price,
        cost: cost || '',
        margin: margin || '',
        isStandard,
        tenantId: tenantId,
        isLevel1: true,
        hasSubProducts: !isStandard || selectedProducts.length > 0,
        parentId: null, // Level1 PRODUCT HAS NO PARENT
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
      };
      
      // Create in Catalogue collection directly
      const level1ProductDocRef = await addDoc(collection(firestore, "Catalogue"), level1ProductData);
      
      createdDocRefs[1] = level1ProductDocRef;
      console.log(`Created Level1 product with ID: ${level1ProductDocRef.id} (${isStandard ? 'Std' : 'Derived'})`);
      
      // Handle dropdown selections for derived Level1 product
      if (!isStandard && selectedProducts.length > 0) {
        const subcollectionRef = collection(level1ProductDocRef, 'SubCollection1');
        const addedProductKeys = new Set(); // TRACK BY UNIQUE KEYS INSTEAD OF IDS
        
        for (const selectedProduct of selectedProducts) {
          // Skip if this selected product has the same name as Level1 product
          if (selectedProduct.name === name) {
            console.log(`Skipping duplicate product "${name}" - already saved as Level1 product`);
            continue;
          }
          
          // Skip if already added (avoid duplicates) - use uniqueKey for better tracking
          if (addedProductKeys.has(selectedProduct.uniqueKey)) {
            console.log(`Skipping duplicate product "${selectedProduct.name}" - already added (Key: ${selectedProduct.uniqueKey})`);
            continue;
          }
          
          // Add the selected product itself as a reference WITH PROPER PARENT ID AND ORIGINAL ID PRESERVATION
          await addProductReference(selectedProduct, subcollectionRef, level1ProductDocRef.id, addedProductKeys);
          
          // If the selected product is derived, fetch its subcollection and add standard children
          if (!selectedProduct.isStandard) {
            await addDerivedProductChildren(selectedProduct, subcollectionRef, level1ProductDocRef.id, addedProductKeys);
          }
        }
      }
      
      // Handle additional manual Level2+ products
      for (let i = 1; i < products.length; i++) {
    const product = products[i];
    const { name: productName, description, moq, sgst, cgst, igst, gst, hsn, cost, margin, isStandard } = product;
    const computedProductPrice = cost ? (Number(cost) * (1 + (Number(margin || 0) / 100))).toFixed(2) : (product.price || '');
        
        if (productName === level1Product.name) {
          console.log(`Skipping duplicate manual product "${productName}" - same as Level1 product`);
          continue;
        }
        
        // FIND THE CORRECT PARENT - This is crucial!
        let parentRef = null;
        let parentId = null;
        
        // Look for the immediate parent (previous product in the chain)
        for (let j = i - 1; j >= 0; j--) {
          if (createdDocRefs[j + 1]) {
            parentRef = createdDocRefs[j + 1];
            parentId = parentRef.id;
            break;
          }
        }
        
        if (!parentRef) {
          console.error(`No parent found for Level${i + 1} product`);
          continue;
        }
        
        const productData = {
          name: productName,
          description,
          moq,
          sgst: sgst || '',
          cgst: cgst || '',
          igst: igst || '',
          gst: gst || '',
          hsn,
          price: computedProductPrice,
          cost: cost || '',
          margin: margin || '',
          isStandard,
          tenantId: tenantId,
          isLevel1: false,
          hasSubProducts: !isStandard,
          isReference: false,
          parentId: parentId, // PROPERLY SET PARENT ID
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          
        };
        
        const subcollectionName = `SubCollection${i}`;
        
        await setDoc(parentRef, { hasSubProducts: true }, { merge: true });
        
        const additionalData = {
          source: `manual_entry_level${i + 1}_product`
        };
        
        const subcollectionRef = collection(parentRef, subcollectionName);
        const docRef = await addDoc(subcollectionRef, {
          ...productData,
          ...additionalData
        });
        
        createdDocRefs[i + 1] = docRef;
        console.log(`Created Level${i + 1} product with ID: ${docRef.id} in ${subcollectionName} (${isStandard ? 'Std' : 'Derived'}) - Parent: ${parentId}`);
        
        if (isStandard) {
          break;
        }
      }
      
      console.log("Products added successfully to Catalogue with proper hierarchical relationships and no ID overlaps");
      await refreshExistingProducts();

      resetForm();
      
    } catch (error) {
      console.error("Error adding products:", error);
      alert("Failed to create products. Please try again.");
    }

    // FIXED Helper function to add a product reference WITH PROPER ID PRESERVATION
    async function addProductReference(product, subcollectionRef, parentId, addedProductKeys) {
      // Determine the original source path
      let originalSourcePath = '';
      let originalCollectionRef = '';
      
      if (product.source === 'main_collection') {
        originalSourcePath = `Catalogue/${product.originalId}`;
        originalCollectionRef = 'main_collection';
      } else {
        if (product.parentId) {
          const subcollectionName = product.source;
          originalSourcePath = `Catalogue/${product.parentId}/${subcollectionName}/${product.originalId}`;
          originalCollectionRef = `${product.parentId}/${subcollectionName}`;
        } else {
          originalSourcePath = `unknown_source/${product.originalId}`;
          originalCollectionRef = 'unknown_source';
        }
      }
      
  const computedPrice = product.cost ? (Number(product.cost) * (1 + (Number(product.margin || 0) / 100))).toFixed(2) : (product.price || '');

  const referenceData = {
        // Basic product info
        name: product.name,
        description: product.description || '',
        moq: product.moq || '',
        sgst: product.sgst || '',
        cgst: product.cgst || '',
        igst: product.igst || '',
        gst: product.gst || '',
        hsn: product.hsn || '',
  price: computedPrice,
  cost: product.cost || '',
  margin: product.margin || '',
        isStandard: product.isStandard,
        tenantId: tenantId,
        isLevel1: false,
        hasSubProducts: false,
        
        // CRUCIAL: Set the parentId for proper tree structure
        parentId: parentId,
        
        // Reference fields - PRESERVE ORIGINAL IDS
        originalProductId: product.originalId, // USE ORIGINAL ID, NOT CURRENT ID
        originalSourcePath: originalSourcePath,
        originalCollectionRef: originalCollectionRef,
        originalParentId: product.parentId || null,
        originalParentName: product.parentName || null,
        isReference: true,
        
        // Current hierarchy info
        source: originalSourcePath,
        referenceType: 'dropdown_selection',
        uniqueReferenceKey: `${parentId}_ref_${product.uniqueKey}`, // UNIQUE REFERENCE KEY
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const referenceDocRef = await addDoc(subcollectionRef, referenceData);
      
      addedProductKeys.add(product.uniqueKey); // TRACK BY UNIQUE KEY
      console.log(`Added reference to product: ${product.name} with Original ID: ${product.originalId}, New Reference ID: ${referenceDocRef.id} - Parent: ${parentId}`);
    }

    // FIXED Helper function to add children of derived products WITH PROPER ID PRESERVATION
    async function addDerivedProductChildren(derivedProduct, subcollectionRef, parentId, addedProductKeys) {
      try {
        console.log(`Fetching Level2+ products of derived product: ${derivedProduct.name} (Original ID: ${derivedProduct.originalId})`);
        
        let sourceDocRef;
        let subcollectionName;
        
        // Determine where to look for the subcollection in Catalogue
        if (derivedProduct.source === 'main_collection') {
          // Product is from main collection, look for its SubCollection1
          sourceDocRef = doc(firestore, 'Catalogue', derivedProduct.originalId); // USE ORIGINAL ID
          subcollectionName = 'SubCollection1';
        } else {
          // Product is from a subcollection, we need to find its children
          if (derivedProduct.parentId) {
            sourceDocRef = doc(firestore, 'Catalogue', derivedProduct.parentId);
            // Determine next subcollection level based on current source
            const currentSubIndex = parseInt(derivedProduct.source.replace('subcollection', '')) || 1;
            subcollectionName = `SubCollection${currentSubIndex + 1}`;
          } else {
            console.log(`Cannot determine parent for derived product: ${derivedProduct.name}`);
            return;
          }
        }
        
        // Fetch the subcollection
        const childrenCollectionRef = collection(sourceDocRef, subcollectionName);
        const childrenSnapshot = await getDocs(childrenCollectionRef);
        
        if (childrenSnapshot.empty) {
          console.log(`No Level2+ products found for derived product: ${derivedProduct.name}`);
          return;
        }
        
        // Add only standard children (not derived ones)
        for (const childDoc of childrenSnapshot.docs) {
          const childData = childDoc.data();
          const childUniqueKey = `sub_${derivedProduct.originalId}_${subcollectionName.toLowerCase()}_${childDoc.id}`;
          
          // Skip if not standard or already added
          if (!childData.isStandard || addedProductKeys.has(childUniqueKey)) {
            continue;
          }
          
          // Create Level2+ product object for reference
          const computedChildPrice = childData.cost ? (Number(childData.cost) * (1 + (Number(childData.margin || 0) / 100))).toFixed(2) : (childData.price || '');
          const childProduct = {
            id: childDoc.id,
            originalId: childData.originalProductId || childDoc.id, // PRESERVE ORIGINAL ID
            name: childData.name,
            description: childData.description || '',
            moq: childData.moq || '',
            gst: childData.gst || '',
            hsn: childData.hsn || '',
            price: computedChildPrice,
            cost: childData.cost || '',
            margin: childData.margin || '',
            isStandard: childData.isStandard,
            parentId: derivedProduct.originalId, // USE ORIGINAL PARENT ID
            parentName: derivedProduct.name,
            source: subcollectionName.toLowerCase(),
            uniqueKey: childUniqueKey
          };
          
          // Add this Level2+ product as a reference WITH NEW PARENT ID BUT PRESERVED ORIGINAL ID
          await addProductReference(childProduct, subcollectionRef, parentId, addedProductKeys);
        }
        
        console.log(`Finished adding Level2+ products of derived product: ${derivedProduct.name}`);
        
      } catch (error) {
        console.error(`Error fetching Level2+ products for derived product ${derivedProduct.name}:`, error);
      }
    }
  };
  
  // Check if we can add more products
  const canAddMoreProducts = () => {
    if (products.length === 0) return false;
    if (products.length >= 6) return false;
    return true;
  };

  // Helper functions for price range
  const getPriceRange = (price) => {
    const numPrice = parseFloat(price) || 0;
    if (numPrice <= 20) return 1;
    if (numPrice <= 40) return 2;
    if (numPrice <= 60) return 3;
    if (numPrice <= 80) return 4;
    if (numPrice <= 100) return 5;
    return 6;
  };

  const getPriceRangeLabel = (range) => {
    switch (range) {
      case 1: return '0-20';
      case 2: return '21-40';
      case 3: return '41-60';
      case 4: return '61-80';
      case 5: return '81-100';
      case 6: return '100+';
      default: return 'Unknown';
    }
  };

  // Custom filter function for autocomplete
  const customFilterOptions = (options, { inputValue }) => {
    let filteredOptions = [...options];

    // Apply pre-selected filter buttons first
    if (selectedPriceRange) {
      const priceRange = priceRanges.find(p => p.value === selectedPriceRange);
      if (priceRange) {
        filteredOptions = filteredOptions.filter(option => {
          const price = parseFloat(option.price) || 0;
          return price >= priceRange.min && (priceRange.max === Infinity ? true : price <= priceRange.max);
        });
      }
    }

    if (selectedGstValue) {
      filteredOptions = filteredOptions.filter(option => {
        const gst = String(option.gst || '');
        return gst === selectedGstValue;
      });
    }

    if (selectedType) {
      if (selectedType === 'std') {
        filteredOptions = filteredOptions.filter(option => option.isStandard === true);
      } else if (selectedType === 'derived') {
        filteredOptions = filteredOptions.filter(option => option.isStandard === false);
      }
    }

    // Filter by input text as user types
    if (inputValue) {
      const searchTerm = inputValue.toLowerCase().trim();
      
      if (searchTerm.length > 0) {
        filteredOptions = filteredOptions.filter(option => {
          switch (searchFilter) {
            case 'name':
              return option.name && option.name.toLowerCase().includes(searchTerm);
            case 'price':
              const priceStr = String(option.price || '');
              return priceStr.toLowerCase().includes(searchTerm);
            case 'gst':
              const gstStr = String(option.gst || '');
              return gstStr.toLowerCase().includes(searchTerm);
            case 'all':
            default:
              return option.searchText && option.searchText.toLowerCase().includes(searchTerm);
          }
        });

        // Sort results for better UX
        return filteredOptions.sort((a, b) => {
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          
          // 1. Exact matches first
          const aExact = aName === searchTerm;
          const bExact = bName === searchTerm;
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          
          // 2. Names starting with search term
          const aStarts = aName.startsWith(searchTerm);
          const bStarts = bName.startsWith(searchTerm);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          
          // 3. How early the match appears in the name
          const aIndex = aName.indexOf(searchTerm);
          const bIndex = bName.indexOf(searchTerm);
          if (aIndex !== bIndex && aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          
          // 4. Alphabetical order
          return aName.localeCompare(bName);
        });
      }
    }

    // Default sorting when no search input
    if (selectedPriceRange || searchFilter === 'price') {
      return filteredOptions.sort((a, b) => {
        const rangeA = getPriceRange(a.price);
        const rangeB = getPriceRange(b.price);
        if (rangeA !== rangeB) {
          return rangeA - rangeB;
        }
        return parseFloat(a.price || 0) - parseFloat(b.price || 0);
      });
    }

    // Alphabetical sorting for all other cases
    return filteredOptions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  // Custom render option for autocomplete with enhanced ID display
  const renderOption = (props, option, { selected }) => (
    <Box 
      component="li" 
      {...props} 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'left',
        py: 1.5,
        px: 2,
        width: '100%',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <input
          type="checkbox"
          checked={selected}
          readOnly
          style={{ 
            marginRight: '12px',
            cursor: 'pointer'
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
            {option.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Price: ₹{option.price || 'N/A'} | Cost: ₹{option.cost || 'N/A'} | Margin: {option.margin ? option.margin + '%' : 'N/A'} | MOQ: {option.moq || 'N/A'} | GST: {option.gst || 'N/A'}%
            {option.description && ` | ${option.description.substring(0, 40)}...`}
            {option.parentName && ` | Child of: ${option.parentName}`}
          </Typography>
        </Box>
      </Box>
      
      <Chip
        size="small"
        label={option.isStandard ? 'Std' : 'Derived'}
        color={option.isStandard ? 'success' : 'warning'}
        variant="outlined"
        sx={{ 
          minWidth: '60px',
          fontSize: '0.75rem',
          height: '24px'
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: '800px', mx: 'auto' }}>
      {/* Header and View Products Button aligned */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Product Level Manager</Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate('/ViewProducts')}
        >
          View All Products
        </Button>
      </Box>
      
      {/* Information Box */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>How it works (ID Matching Filter Applied):</strong><br/>
          • <strong>Std Product:</strong> Complete, standalone product stored in Catalogue collection<br/>
          • <strong>Derived Product:</strong> Has sub-products/variations stored in subcollections (SubCollection1, SubCollection2, etc.)<br/>
          • <strong>NEW - ID Matching Filter:</strong> Only products where Current ID = Original ID are shown in dropdown<br/>
          • <strong>Original Products Only:</strong> Ensures you're selecting authentic, non-moved products from their original locations<br/>
          • <strong>Hierarchy Integrity:</strong> Maintains proper parent-child relationships without ID overlaps<br/>
          • <strong>Dynamic Forms:</strong> Forms adjust based on product type (Std/Derived) and level<br/>
          • <strong>Validation:</strong> All fields are required to ensure complete product information<br/>

        </Typography>
      </Paper>
      
      {/* Product Forms */}
      {products.map((product, index) => {
        const isLevel1ProductDerived = products[0]?.isStandard === false;

        return (
          <React.Fragment key={index}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3, 
                mb: 4, 
                position: 'relative',
                borderRadius: 1,
                border: product.isStandard ? '2px solid #4CAF50' : '2px solid #FF9800'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {index === 0 ? 'Level1 Product' : `Level${index + 1} Product`}
                  <Typography 
                    component="span" 
                    variant="caption" 
                    sx={{ 
                      ml: 1, 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1, 
                      backgroundColor: product.isStandard ? '#4CAF50' : '#FF9800',
                      color: 'white',
                      fontSize: '0.7rem'
                    }}
                  >
                    {product.isStandard ? 'STD' : 'DERIVED'}
                  </Typography>
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={product.isStandard}
                        onChange={(e) => handleChange(index, 'isStandard', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={product.isStandard ? "Std" : "Derived"}
                    sx={{ mr: 1 }}
                  />
                  
                  {index > 0 && (
                    <IconButton 
                      color="error" 
                      onClick={() => removeProduct(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
              
              <TextField
                fullWidth
                placeholder={index === 0 ? "Enter Level1 product name *" : `Enter Level${index + 1} product name *`}
                variant="outlined"
                value={product.name}
                onChange={(e) => handleChange(index, 'name', e.target.value)}
                error={product.nameError}
                helperText={product.nameError ? "Product name is required" : ""}
                sx={{ mb: 3 }}
              />
              
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Product Details (All fields are required *)
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    placeholder="Description *"
                    variant="outlined"
                    multiline
                    rows={4}
                    value={product.description}
                    onChange={(e) => handleChange(index, 'description', e.target.value)}
                    error={product.descriptionError}
                    helperText={product.descriptionError ? "Description is required" : ""}
                  />
                </Grid>

                

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="SGST % *"
                    variant="outlined"
                    value={product.sgst}
                    onChange={(e) => handleChange(index, 'sgst', e.target.value)}
                    error={product.sgstError}
                    helperText={product.sgstError ? "SGST is required" : ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="CGST % *"
                    variant="outlined"
                    value={product.cgst}
                    onChange={(e) => handleChange(index, 'cgst', e.target.value)}
                    error={product.cgstError}
                    helperText={product.cgstError ? "CGST is required" : ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="IGST % *"
                    variant="outlined"
                    value={product.igst}
                    onChange={(e) => handleChange(index, 'igst', e.target.value)}
                    error={product.igstError}
                    helperText={product.igstError ? "IGST is required" : ""}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="MoQ *"
                    variant="outlined"
                    value={product.moq}
                    onChange={(e) => handleChange(index, 'moq', e.target.value)}
                    error={product.moqError}
                    helperText={product.moqError ? "MoQ is required" : ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="HSN *" 
                    variant="outlined"
                    value={product.hsn}
                    onChange={(e) => handleChange(index, 'hsn', e.target.value)}
                    error={product.hsnError}
                    helperText={product.hsnError ? "HSN is required" : ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Cost"
                    variant="outlined"
                    value={product.cost}
                    onChange={(e) => handleChange(index, 'cost', e.target.value)}
                    error={product.costError}
                    helperText={product.costError ? "Cost is required" : ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Margin (%)"
                    variant="outlined"
                    value={product.margin}
                    onChange={(e) => handleChange(index, 'margin', e.target.value)}
                    error={product.marginError}
                    helperText={product.marginError ? "Margin is required" : ""}
                  />
                </Grid>

                {/* Computed price (plain text) = cost * (1 + margin/100) - displayed after Cost and Margin */}
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  {(() => {
                    const costVal = parseFloat(product.cost || 0);
                    const marginVal = parseFloat(product.margin || 0);
                    const computed = costVal > 0 ? (costVal * (1 + (isNaN(marginVal) ? 0 : marginVal / 100))).toFixed(2) : (product.price || '');
                    return (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Price</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                          {computed ? `₹${computed}` : 'N/A'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Grid>
              </Grid>
            </Paper>

            {/* Searchable Autocomplete Dropdown - Show only when Level1 product is derived */}
            {index === 0 && isLevel1ProductDerived && (
              <Box sx={{ mb: 3 }}>
                {/* Filter Buttons */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Filter Products (Original IDs Only - References Excluded)
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={refreshExistingProducts}
                      disabled={loadingProducts}
                      startIcon={loadingProducts ? <CircularProgress size={16} /> : null}
                      sx={{ borderRadius: '20px', textTransform: 'none' }}
                    >
                      {loadingProducts ? 'Refreshing...' : 'Refresh Products'}
                    </Button>
                  </Box>
                  
                  {/* Horizontal Filter Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant={searchFilter === 'name' ? 'contained' : 'outlined'}
                      onClick={() => setSearchFilter('name')}
                      sx={{ borderRadius: '20px', textTransform: 'none' }}
                    >
                      Category
                    </Button>

                    <Button
                      variant={selectedPriceRange ? 'contained' : 'outlined'}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handlePriceMenuOpen}
                      sx={{ 
                        borderRadius: '20px', 
                        textTransform: 'none',
                        backgroundColor: selectedPriceRange ? 'primary.main' : 'transparent',
                        color: selectedPriceRange ? 'white' : 'inherit'
                      }}
                    >
                      Price {selectedPriceRange && `(${selectedPriceRange})`}
                    </Button>

                    <Button
                      variant={selectedGstValue ? 'contained' : 'outlined'}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleGstMenuOpen}
                      sx={{ 
                        borderRadius: '20px', 
                        textTransform: 'none',
                        backgroundColor: selectedGstValue ? 'primary.main' : 'transparent',
                        color: selectedGstValue ? 'white' : 'inherit'
                      }}
                    >
                      GST {selectedGstValue && `(${selectedGstValue}%)`}
                    </Button>

                    <Button
                      variant={selectedType ? 'contained' : 'outlined'}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleTypeMenuOpen}
                      sx={{ 
                        borderRadius: '20px', 
                        textTransform: 'none',
                        backgroundColor: selectedType ? (selectedType === 'std' ? 'success.main' : 'warning.main') : 'transparent',
                        color: selectedType ? 'white' : 'inherit'
                      }}
                    >
                      Type {selectedType && `(${selectedType === 'std' ? 'Std' : 'Derived'})`}
                    </Button>

                    <Button
                      variant="outlined"
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleAllFiltersMenuOpen}
                      sx={{ borderRadius: '20px', textTransform: 'none' }}
                    >
                      All filters ⚙️
                    </Button>
                  </Box>

                  {/* Price Menu */}
                  <Menu
                    anchorEl={priceMenuAnchor}
                    open={Boolean(priceMenuAnchor)}
                    onClose={handlePriceMenuClose}
                    PaperProps={{ sx: { minWidth: '200px' } }}
                  >
                    <MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Select Price Range
                    </MenuItem>
                    <Divider />
                    {priceRanges.map((range) => (
                      <MenuItem
                        key={range.value}
                        onClick={() => handlePriceSelect(range.value)}
                        selected={selectedPriceRange === range.value}
                      >
                        <AttachMoneyIcon fontSize="small" sx={{ mr: 1 }} />
                        {range.label}
                      </MenuItem>
                    ))}
                    {selectedPriceRange && (
                      <>
                        <Divider />
                        <MenuItem onClick={() => handlePriceSelect('')} sx={{ color: 'error.main' }}>
                          Clear Price Filter
                        </MenuItem>
                      </>
                    )}
                  </Menu>

                  {/* GST Menu */}
                  <Menu
                    anchorEl={gstMenuAnchor}
                    open={Boolean(gstMenuAnchor)}
                    onClose={handleGstMenuClose}
                    PaperProps={{ sx: { minWidth: '200px' } }}
                  >
                    <MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Select GST Rate
                    </MenuItem>
                    <Divider />
                    {gstOptions.map((gst) => (
                      <MenuItem
                        key={gst.value}
                        onClick={() => handleGstSelect(gst.value)}
                        selected={selectedGstValue === gst.value}
                      >
                        <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
                        {gst.label}
                      </MenuItem>
                    ))}
                    {selectedGstValue && (
                      <>
                        <Divider />
                        <MenuItem onClick={() => handleGstSelect('')} sx={{ color: 'error.main' }}>
                          Clear GST Filter
                        </MenuItem>
                      </>
                    )}
                  </Menu>

                  {/* Type Menu */}
                  <Menu
                    anchorEl={typeMenuAnchor}
                    open={Boolean(typeMenuAnchor)}
                    onClose={handleTypeMenuClose}
                    PaperProps={{ sx: { minWidth: '200px' } }}
                  >
                    <MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Select Product Type
                    </MenuItem>
                    <Divider />
                    {typeOptions.map((type) => (
                      <MenuItem
                        key={type.value}
                        onClick={() => handleTypeSelect(type.value)}
                        selected={selectedType === type.value}
                      >
                        {type.value === 'std' ? (
                          <VerifiedIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                        ) : (
                          <CallSplitIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                        )}
                        {type.label}
                      </MenuItem>
                    ))}
                    {selectedType && (
                      <>
                        <Divider />
                        <MenuItem onClick={() => handleTypeSelect('')} sx={{ color: 'error.main' }}>
                          Clear Type Filter
                        </MenuItem>
                      </>
                    )}
                  </Menu>

                  {/* All Filters Menu */}
                  <Menu
                    anchorEl={allFiltersMenuAnchor}
                    open={Boolean(allFiltersMenuAnchor)}
                    onClose={handleAllFiltersMenuClose}
                    PaperProps={{ sx: { minWidth: '250px' } }}
                  >
                    <MenuItem disabled sx={{ fontWeight: 600, color: 'primary.main' }}>
                      All Filter Options
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => setSearchFilter('name')}>
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      Search by Name
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter('all')}>
                      <SearchIcon fontSize="small" sx={{ mr: 1 }} />
                      Search All Fields
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={clearAllFilters} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      Clear All Filters
                    </MenuItem>
                  </Menu>

                  {/* Active Filters Display */}
                  {(selectedPriceRange || selectedGstValue || selectedType) && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <Typography variant="caption" sx={{ alignSelf: 'center', fontWeight: 600 }}>
                        Active filters:
                      </Typography>
                      {selectedPriceRange && (
                        <Chip
                          label={`Price: ${selectedPriceRange}`}
                          size="small"
                          onDelete={() => handlePriceSelect('')}
                          color="primary"
                        />
                      )}
                      {selectedGstValue && (
                        <Chip
                          label={`GST: ${selectedGstValue}%`}
                          size="small"
                          onDelete={() => handleGstSelect('')}
                          color="primary"
                        />
                      )}
                      {selectedType && (
                        <Chip
                          label={`Type: ${selectedType === 'std' ? 'Std' : 'Derived'}`}
                          size="small"
                          onDelete={() => handleTypeSelect('')}
                          color={selectedType === 'std' ? 'success' : 'warning'}
                        />
                      )}
                    </Box>
                  )}
                </Box>

                <Autocomplete
                  ref={autocompleteRef}
                  multiple
                  options={existingProducts}
                  value={selectedProducts}
                  onChange={handleProductSelect}
                  getOptionLabel={(option) => option.name || ''}
                  filterOptions={customFilterOptions}
                  renderOption={renderOption}
                  loading={loadingProducts}
                  loadingText="Loading original products only (ID matched)..."
                  noOptionsText="No original products found (only showing products with matching Current/Original IDs)"
                  clearOnEscape
                  selectOnFocus
                  clearText="Clear all selections"
                  openText="Open dropdown"
                  closeText="Close dropdown"
                  disableCloseOnSelect
                  filterSelectedOptions={false}
                  includeInputInList={true}
                  autoHighlight={true}
                  blurOnSelect={false}
                  isOptionEqualToValue={(option, value) => option.uniqueKey === value.uniqueKey}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Search Products ${(selectedPriceRange || selectedGstValue || selectedType) ? '(Filters Applied)' : ''}`}
                      placeholder={
                        (selectedPriceRange || selectedGstValue || selectedType) 
                          ? 'Products filtered - type to search within products'
                          : searchFilter === 'name' 
                            ? 'Type product name to search...'
                            : 'Type to search all fields...'
                      }
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-listbox': {
                      maxHeight: '300px'
                    }
                  }}
                />
                
                {/* Show selected products with enhanced ID information */}
                {selectedProducts.length > 0 && (
                  <Paper sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Original Products ({selectedProducts.length}) - All ID Verified:
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      {selectedProducts.map((selectedProduct, idx) => (
                        <Paper 
                          key={selectedProduct.uniqueKey} 
                          sx={{ 
                            p: 2, 
                            mb: 2, 
                            border: '1px solid #ddd',
                            backgroundColor: 'white',
                            position: 'relative'
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updatedSelection = selectedProducts.filter((_, i) => i !== idx);
                              setSelectedProducts(updatedSelection);
                            }}
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              color: 'error.main'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>

                          <Typography variant="h6" sx={{ mb: 1, pr: 4 }}>
                            {selectedProduct.name}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ 
                            wordBreak: 'break-word',
                            lineHeight: 1.4
                          }}>
                            <strong>Type:</strong> {selectedProduct.isStandard ? 'Standard' : 'Derived'}, 
                            <strong> Price:</strong> ₹{selectedProduct.price || 'N/A'}, 
                            <strong> Cost:</strong> ₹{selectedProduct.cost || 'N/A'}, 
                            <strong> Margin:</strong> {selectedProduct.margin ? selectedProduct.margin + '%' : 'N/A'}, 
                            <strong> MOQ:</strong> {selectedProduct.moq || 'N/A'}, 
                            <strong> GST:</strong> {selectedProduct.gst || 'N/A'}%, 
                            <strong> HSN:</strong> {selectedProduct.hsn || 'N/A'}, 
                            <strong> Description:</strong> {selectedProduct.description ? (
                              selectedProduct.description.length > 50 
                                ? `${selectedProduct.description.substring(0, 50)}...` 
                                : selectedProduct.description
                            ) : 'N/A'}
                            {selectedProduct.parentName && (
                              <>
                                , <strong> Child of:</strong> {selectedProduct.parentName}
                              </>
                            )}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={selectedProduct.isStandard ? 'Standard' : 'Derived'}
                              color={selectedProduct.isStandard ? 'success' : 'warning'}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={selectedProduct.source === 'main_collection' ? 'Level1' : 'Level2+'}
                              color="default"
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`₹${selectedProduct.price || 'N/A'}`}
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`Cost: ₹${selectedProduct.cost || 'N/A'}`}
                              color="info"
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`Margin: ${selectedProduct.margin ? selectedProduct.margin + '%' : 'N/A'}`}
                              color="secondary"
                              variant="outlined"
                            />
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                    
                    {selectedProducts[0] && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ddd' }}>
                        <Typography variant="subtitle2" gutterBottom color="success.main">
                          ✅ Note: All selected products are verified originals with matching Current/Original IDs ({selectedProducts[0].originalId}). References and moved products are automatically excluded.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            )}
          </React.Fragment>
        );
      })}
      
      {/* Form Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addProduct}
          sx={{ mb: 2 }}
          disabled={!canAddMoreProducts()}
        >
          ADD PRODUCT
        </Button>
        
        <Box>
          <Button
            variant="contained"
            color="error"
            onClick={resetForm}
            sx={{ mx: 1 }}
          >
            RESET
          </Button>
          
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
          >
            SUBMIT
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductHierarchyManager;